import { ArrowRight, ChevronLeft, Clock } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { OnboardingStepMeta } from "@/features/onboarding/steps-meta";
import { cn } from "@/lib/utils";

interface HubStepShellProps {
  meta: OnboardingStepMeta;
  index: number;
  onBack: () => void;
  /** Mark the step skipped and advance (no save). Omit to hide the Skip button. */
  onSkip?: () => void;
  isFirst: boolean;
  submitting: boolean;
  children: ReactNode;
  /** Optional right-hand column (e.g. the live letterhead preview). */
  preview?: ReactNode;
  continueLabel?: string;
}

/**
 * The detail-panel frame shared by every step of the Getting-started hub: a
 * header band ("STEP n OF 6" + title + description + time), a body (the step's
 * fields, optionally split with a preview column), and a Back / Skip / Save &
 * continue footer. "Save & continue" is a `type="submit"`, so render this inside
 * the step's `<form>` / Formik `<Form>` — submitting runs the save-then-advance.
 */
export function HubStepShell({
  meta,
  index,
  onBack,
  onSkip,
  isFirst,
  submitting,
  children,
  preview,
  continueLabel = "Save & continue",
}: HubStepShellProps) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-6 py-5 sm:px-7">
        <div className="flex flex-col gap-1">
          <span className="text-warning text-xs font-semibold tracking-[0.08em] uppercase">
            Step {index + 1} of 6
          </span>
          <h2 className="text-[1.6rem] leading-tight tracking-tight">{meta.detailTitle}</h2>
          <p className="text-muted-foreground max-w-xl text-sm">{meta.detailDescription}</p>
        </div>
        <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
          <Clock className="size-3.5" /> About {meta.minutes} minute{meta.minutes === 1 ? "" : "s"}
        </span>
      </div>

      <div className={cn(preview && "grid md:grid-cols-2")}>
        <div className={cn("flex flex-col gap-4 p-6 sm:p-7", preview && "md:border-r")}>
          {children}
        </div>
        {preview && <div className="bg-muted/40 flex flex-col gap-3 p-6 sm:p-7">{preview}</div>}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4 sm:px-7">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={isFirst || submitting}
          className="text-muted-foreground"
        >
          <ChevronLeft className="size-4" /> Back
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {onSkip && (
            <Button type="button" variant="outline" onClick={onSkip} disabled={submitting}>
              Skip for now
            </Button>
          )}
          <Button type="submit" variant="cta" disabled={submitting}>
            {submitting ? "Saving…" : continueLabel}
            {!submitting && <ArrowRight className="size-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}
